<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { UserRoleType } from '@/api/generated/api.enum';
import { TUserOrInvitation, useUsers } from '@/composables/schoolAdmin/useUsers';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useInvitations } from '@/composables/useInvitations';
import { usePermissions } from '@/composables/usePermissions';
import { useRouteContext } from '@/composables/useRouteContext';
import { useWhoAmI } from '@/composables/useWhoAmI';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import UserAccessRightDisplayListItem from '@/core/list-items/UserAccessRightDisplayListItem.vue';
import UserAccessRightListItem from '@/core/list-items/UserAccessRightListItem.vue';
import UserSearchSelect from '@/core/selects/UserSearchSelect.vue';
import { IGenericUser, IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { isClassAccessRole } from '@/utils/role.utils';
import { createFakeUserFromInvitation } from '@/utils/user.utils';
import { toRecord } from 'common/utils/array.utils';
import { now } from 'common/utils/date.utils';
import { normalizeEmail } from 'common/utils/string.utils';
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { usersAndInvitations, amIOnlyActiveUserInTenant } = useUsers();
const { whoAmI } = useWhoAmI();
const { canManageAccessRights } = usePermissions();
const { tContainer } = useContainerTranslations();
const { confirm } = useConfirmDialog();
const { isClassContext } = useRouteContext();
const { t } = useI18n();
const { PENDING_INVITATION_ID } = useInvitations();

const emit = defineEmits<{
  'update:isValid': [value: boolean];
  'update:selectedUsers': [value: IUserWithPermissions[]];
  continue: [value: IUserWithPermissions[]];
}>();

const props = withDefaults(
  defineProps<{
    currentTeam: IUserWithPermissions[];
    availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
    userListItemMode?: 'edit' | 'archive';
    userListItemType?: 'remove' | 'plain';
    allowInlineAddingUsers?: boolean;
  }>(),
  {
    userListItemType: 'remove',
    userListItemMode: 'edit',
  },
);
const { currentTeam } = toRefs(props);

const selectedUsers = ref<IUserWithPermissions[]>([]);
const pendingInvitationUsers = ref<string[]>([]);
const userAccessRightListItemRefs =
  useTemplateRef<InstanceType<typeof UserAccessRightListItem>[]>('userAccessRightListItemRef');

const validityByUserId = ref<Record<number, boolean>>({});
const currentTeamEmails = computed<Set<string>>(
  () => new Set(currentTeam.value.map((teamMember) => normalizeEmail(teamMember.user.email))),
);

const allUsersValid = computed(() => {
  return selectedUsers.value.every((user) => validityByUserId.value[user.user.id] !== false);
});

const emitCurrentValidity = (): void => emit('update:isValid', allUsersValid.value && selectedUsers.value.length > 0);

const syncValidityByUserId = (): void => {
  const prevValidityByUserId = validityByUserId.value;
  validityByUserId.value = toRecord(
    selectedUsers.value,
    (user) => user.user.id,
    (user) => prevValidityByUserId[user.user.id] ?? true,
  );
};

const onSubmit = async (): Promise<void> => {
  emit('continue', selectedUsers.value);
};

const handleListItemCollapse = (): void => {
  userAccessRightListItemRefs.value?.forEach((accordion) => {
    accordion?.collapse();
  });
  userAccessRightListItemRefs.value?.[userAccessRightListItemRefs.value.length - 1]?.decollapse();
};

const resolveUserId = (entry: TUserOrInvitation): number => {
  return entry.userRole ? entry.user.id! : entry.invitation?.id || 0;
};

const usersAndInvitationsAndPendingInvitations = computed<TUserOrInvitation[]>(() => {
  const base = usersAndInvitations.value;
  const existingEmails = new Set(base.map((entry) => entry.user.email ?? ''));

  const pendingEntries = pendingInvitationUsers.value
    .filter((email) => !!email && !existingEmails.has(email))
    .map((email) => ({
      roleType: UserRoleType.user,
      user: {
        firstName: '',
        lastName: '',
        email,
        isInvitation: true,
        isPendingInvitation: true,
      },
      invitation: undefined,
      userRole: undefined,
      pendingId: PENDING_INVITATION_ID,
    }))
    .map((entry) => ({
      roleType: entry.roleType,
      user: {
        ...entry.user,
        id: entry.pendingId,
      },
      invitation: entry.invitation,
      userRole: entry.userRole,
    }));

  return [...base, ...pendingEntries];
});

const onUserSelected = async (user: IGenericUser | null): Promise<void> => {
  if (user?.isPendingInvitation && user.email && !pendingInvitationUsers.value.includes(user.email)) {
    pendingInvitationUsers.value.push(user.email);
  }

  const userData = transFormedUserRoles.value.find((u) => u.user.email === user?.email);
  if (!userData) {
    console.error('Selected user not found in transformed user roles:', user);
    return;
  }

  selectedUsers.value = [userData, ...selectedUsers.value.filter((u) => u.user.email !== userData.user.email)];
  syncValidityByUserId();
  emit('update:selectedUsers', selectedUsers.value);
  emitCurrentValidity();

  await nextTick();
  handleListItemCollapse();
};

const onUserRemoved = async (user: IUserWithPermissions): Promise<void> => {
  const confirmed = await confirm({
    title: t('accessRights.removeTitle'),
    description: tContainer('teamMemberRemoveDescription'),
    confirmButtonLabel: t('accessRights.removeButton'),
    declineButtonLabel: t('general.cancel'),
  });
  if (!confirmed) return;

  const isPendingInvitation = user.isPendingInvitation || user.user.id === PENDING_INVITATION_ID;
  if (isPendingInvitation && user.user.email) {
    const normalizedEmail = normalizeEmail(user.user.email);
    pendingInvitationUsers.value = pendingInvitationUsers.value.filter(
      (email) => normalizeEmail(email) !== normalizedEmail,
    );
  }

  selectedUsers.value = selectedUsers.value.filter((u) => u.user.email !== user.user.email);
  syncValidityByUserId();
  emit('update:selectedUsers', selectedUsers.value);
  emitCurrentValidity();
};

const onUserUpdated = (user: IUserWithPermissions): void => {
  const userIndex = selectedUsers.value.findIndex((u) => u.user.email === user.user.email);
  if (userIndex === -1) return;
  selectedUsers.value[userIndex] = user;
  emit('update:selectedUsers', selectedUsers.value);
  emitCurrentValidity();
};

const availableUsersAndInvitations = computed<IGenericUser[]>(() => {
  const selectedEmails = selectedUsers.value.map((su) => su.user.email);

  return usersAndInvitationsAndPendingInvitations.value
    .filter((entry) => {
      const email = entry.user.email ?? '';

      if (selectedEmails.includes(email)) return false;

      if (entry.invitation) {
        const currentDate = now();
        const activeUntil = new Date(entry.invitation.activeUntil);
        const isExpired = currentDate > activeUntil;
        if (isExpired) return false;
      }

      return true;
    })
    .map((entry) => ({
      ...entry.user,
      id: resolveUserId(entry),
    }));
});

const transFormedUserRoles = computed<IUserWithPermissions[]>(() => {
  return usersAndInvitationsAndPendingInvitations.value.map((entry) => {
    const isRealUser = !!entry.userRole;
    const id = entry.user.id ?? resolveUserId(entry);

    const user: Components.Schemas.UserDataDto = isRealUser
      ? (entry.user as Components.Schemas.UserDataDto)
      : createFakeUserFromInvitation(id, entry.user.email ?? '');

    return {
      user,
      userRoleType: entry.roleType,
      permissions: {
        canManageAccessRights: true,
        canManageStudents: true,
        canManagePerformanceAssessments: true,
        canManageCompetences: true,
        schoolSubjectIds: [],
      },
      schoolSubjects: [],
      userRole: entry.roleType,
      schoolClass: null,
      isInvitation: !isRealUser,
      isPendingInvitation: entry.user.isPendingInvitation,
    };
  });
});

const onListItemValidityChanged = (userId: number, isValid: boolean): void => {
  validityByUserId.value = { ...validityByUserId.value, [userId]: isValid };
  emitCurrentValidity();
};

const isDisabled = (user: IUserWithPermissions): boolean => {
  return (isClassContext.value && whoAmI.value?.id === user.user.id) || !isClassAccessRole(user.userRoleType);
};

const hideRemoveButton = (user: IUserWithPermissions): boolean => {
  // Only Admins and Owners can remove themselves. If !isClassContext -> Admin/Owner view
  return isClassContext.value && whoAmI.value?.id === user.user.id && isClassAccessRole(user.userRoleType);
};

const isNewAccessRight = (user: IUserWithPermissions): boolean =>
  !currentTeamEmails.value.has(normalizeEmail(user.user.email));

watch(
  currentTeam,
  () => {
    selectedUsers.value = [...currentTeam.value];
    syncValidityByUserId();
    emitCurrentValidity();
  },
  { immediate: true },
);

watch(
  [amIOnlyActiveUserInTenant, transFormedUserRoles],
  () => {
    if (!amIOnlyActiveUserInTenant.value) return;
    if (selectedUsers.value.length > 0) return;

    const currentUserEntry = transFormedUserRoles.value.find((u) => u.user.id === whoAmI.value?.id);
    if (!currentUserEntry) return;

    selectedUsers.value = [currentUserEntry];
    syncValidityByUserId();
    emit('update:selectedUsers', selectedUsers.value);
    emitCurrentValidity();
  },
  { immediate: true },
);

defineExpose({
  onSubmit,
});
</script>

<template>
  <form class="grid h-full grid-rows-[auto_auto_1fr_auto] px-lg md:px-content" @submit.prevent="onSubmit">
    <p class="text-subtitle mb-md">
      {{ canManageAccessRights ? $t('classes.teamDescription') : tContainer('teamMembersDescriptionAlt') }}
    </p>
    <UserSearchSelect
      v-if="canManageAccessRights"
      :user-options="availableUsersAndInvitations"
      :selected-users="selectedUsers"
      :label="$t('classes.teamSelectLabel')"
      :placeholder="$t('classes.teamSelectPlaceholder')"
      class="mb-lg"
      :allow-inline-add="allowInlineAddingUsers"
      @user-selected="onUserSelected"
    />
    <ul v-if="selectedUsers.length" class="flex flex-col gap-sm min-w-0">
      <template v-for="tenantUser in selectedUsers" :key="tenantUser.user.email">
        <UserAccessRightListItem
          v-if="canManageAccessRights"
          ref="userAccessRightListItemRef"
          :disabled="isDisabled(tenantUser)"
          :hide-remove-button="hideRemoveButton(tenantUser)"
          :is-new-access-right="isNewAccessRight(tenantUser)"
          :available-school-subjects="availableSchoolSubjects"
          :available-school-subject-groups="availableSchoolSubjectGroups"
          :type="userListItemType"
          :mode="userListItemMode"
          :user="tenantUser"
          @click:remove="onUserRemoved"
          @update:user="onUserUpdated"
          @update:is-valid="(val) => onListItemValidityChanged(tenantUser.user.id, val)"
        />
        <UserAccessRightDisplayListItem v-else :user="tenantUser" />
      </template>
    </ul>

    <EmptyPlaceholder v-else :data="selectedUsers" />
  </form>
</template>
